class Tab extends EventTarget {
    #tabElement;
    #tabListElements;
    #activeTab;
    #activeTabPane;
    #duration = 0;
    #id;

    constructor({tabListElement}) {
        super();
        this.tabElement = tabListElement;
        this.id = this.#tabElement.id;
        this.tabListElements = this.tabElement.querySelectorAll('[role="tab"]');
        this.duration = this.tabElement.getAttribute('data-duration');
        this.main();
    }

    main() {
        this.attachClickListeners();
        this.setupTabListState();
        this.show();
    }

    async handleTabListElement({currentTarget}) {
        if (this.activeTab === currentTarget) return;
        await this.hide();
        this.updateTabListState(currentTarget);
        await this.show();
        this.emit();
    }

    setupTabListState() {
        this.activeTab = [...this.tabListElements].find(e => e.classList.contains('active'));
        this.activeTabPane = document.getElementById(this.activeTab.dataset.tabTarget);
    }

    attachClickListeners() {
        for (const tabListElement of this.tabListElements) {
            tabListElement.addEventListener('click', (e) => this.handleTabListElement(e), false);
        }
    }

    updateTabListState(currentTarget) {
        const tabElement = document.getElementById(currentTarget.dataset.tabTarget);
        this.activeTab = currentTarget;
        this.activeTabPane = tabElement;
    }

    hide() {
        return new Promise((resolve) => {
            this.activeTabPane.classList.remove('entered');
            this.activeTabPane.classList.add('leaving');
            setTimeout(() => {
                this.activeTab.classList.remove('active');
                this.activeTabPane.classList.remove('active');
                this.activeTabPane.classList.remove('leaving');
                resolve();
            }, this.duration);
        });
    }

    show() {
        return new Promise((resolve) => {
            this.activeTab.classList.add('active');
            this.activeTabPane.classList.add('active');
            setTimeout(() => {
                this.activeTabPane.classList.add('entered');
                resolve();
            }, 0);
        });
    }

    emit() {
        this.dispatchEvent(
            new CustomEvent('tab.change', {
                detail: {
                    activeTab: this.activeTab,
                    id: this.id
                }
            })
        )
    }


    get activeTab() {
        return this.#activeTab;
    }

    set activeTab(value) {
        if (value === undefined) {
            // Set the first tab list element as active
            this.#activeTab = [...this.tabListElements].at(0);
        } else {
            this.#activeTab = value;
        }
    }

    get activeTabPane() {
        return this.#activeTabPane;
    }

    set activeTabPane(value) {
        this.#activeTabPane = value;
    }

    /**
     *
     * @param nodeList {NodeListOf<HTMLElementTagNameMap[keyof HTMLElementTagNameMap]>}
     */
    set tabListElements(nodeList) {
        if ([...nodeList].every(node => node.hasAttribute('data-tab-target')) && nodeList.length) {
            this.#tabListElements = nodeList;
        } else {
            throw new Error('Tab list elements must have "data-tab-target={tabPaneId}" and role="tab" attribute')
        }
    }

    /**
     *
     * @return {NodeListOf<HTMLElementTagNameMap[keyof HTMLElementTagNameMap]>}
     */
    get tabListElements() {
        return this.#tabListElements;
    }


    get duration() {
        return this.#duration;
    }

    set duration(value) {
        if (value !== null) {
            this.#duration = Number.parseInt(value);
        } else {
            this.#duration = 400;
        }
    }

    get tabElement() {
        return this.#tabElement;
    }

    set tabElement(value) {
        this.#tabElement = value;
    }

    get id() {
        return this.#id;
    }

    set id(value) {
        if (value === '') {
            throw new Error('Tablist element with role="tablist" must have a valid id');
        } else {
            this.#id = value;
        }
    }
}

export default function useTab() {
    for (const tabListElement of document.querySelectorAll('[role="tablist"]')) {
        new Tab({
            tabListElement
        });
    }
}
